/**
 * Gateways de pagamento da plataforma — trilha T9.
 *
 * O catálogo que a tela de Integrações mostra ao operador e as leituras que os
 * adaptadores do Supabase compartilham. Os contratos de cada gateway — endpoints,
 * eventos e verificação do aviso — estão citados, com a página da documentação
 * oficial, no topo dos adaptadores em `supabase/functions/_compartilhado/gateways/`.
 */

import type { Fatura, InadimplenciaDaClinica, ProvedorDeGateway } from './tipos';

export interface DescricaoDoGateway {
    id: ProvedorDeGateway;
    nome: string;
    /** Como a clínica paga pelo link. */
    comoPaga: string;
    chave: { rotulo: string; dica: string };
    segredo: { rotulo: string; dica: string; exigido: boolean };
    /**
     * A chave publicável, quando o gateway tem uma. Só a Stripe tem nesta trilha, e ela
     * é pedida porque o cartão do autocadastro é tokenizado no navegador — sem `pk_` o
     * formulário do cartão não carrega.
     */
    publicavel?: { rotulo: string; dica: string };
    /** Os eventos a assinar no painel do gateway. */
    eventos: string;
    documentacao: string;
}

export const GATEWAYS_DE_PAGAMENTO: DescricaoDoGateway[] = [
    {
        id: 'asaas',
        nome: 'Asaas',
        comoPaga: 'Fatura do Asaas, em que a clínica escolhe como pagar. O PIX copia-e-cola e a linha digitável aparecem também na tela da clínica, quando o Asaas os devolve.',
        chave: { rotulo: 'Chave de API', dica: 'A de sandbox começa com $aact_hmlg_; a de produção, com $aact_prod_.' },
        segredo: {
            rotulo: 'Token de autenticação do webhook',
            dica: 'O token que você define ao cadastrar o webhook no Asaas. Ele chega no cabeçalho asaas-access-token de cada aviso, e aviso sem ele é recusado.',
            exigido: true,
        },
        eventos: 'PAYMENT_RECEIVED, PAYMENT_CONFIRMED, PAYMENT_OVERDUE, PAYMENT_REFUNDED e PAYMENT_DELETED',
        documentacao: 'https://docs.asaas.com/docs/receba-eventos-do-asaas-no-seu-endpoint-de-webhook.md',
    },
    {
        id: 'stripe',
        nome: 'Stripe',
        comoPaga: 'Checkout da Stripe, com as formas de pagamento ativas no Dashboard da conta. O link vence em 24 horas e é refeito quando a clínica pede de novo.',
        chave: { rotulo: 'Chave secreta', dica: 'A chave secreta da conta, de teste ou de produção. Começa com sk_test_ (teste) ou sk_live_ (produção).' },
        publicavel: {
            rotulo: 'Chave publicável',
            dica: 'A que começa com pk_test_ ou pk_live_. Ela VAI AO NAVEGADOR de quem se cadastra — é assim que o cartão é digitado dentro do formulário da Stripe, sem passar pelo nosso servidor. Não é segredo; a secreta, sim, e nunca vai neste campo.',
        },
        segredo: {
            rotulo: 'Segredo de assinatura do webhook',
            dica: 'O signing secret do endpoint, que começa com whsec_. É com ele que a assinatura Stripe-Signature de cada aviso é conferida.',
            exigido: true,
        },
        eventos: 'checkout.session.completed, checkout.session.async_payment_succeeded, checkout.session.async_payment_failed, checkout.session.expired e charge.refunded',
        documentacao: 'https://docs.stripe.com/webhooks',
    },
    {
        id: 'mercadopago',
        nome: 'Mercado Pago',
        comoPaga: 'Checkout Pro do Mercado Pago, com as formas que a conta aceita.',
        chave: { rotulo: 'Access Token', dica: 'O Access Token das credenciais da aplicação, de teste ou de produção.' },
        segredo: {
            rotulo: 'Assinatura secreta do webhook (opcional)',
            dica: 'Fica guardada, mas ainda não é conferida: a validação do x-signature está pendente. Enquanto isso, o aviso só dá baixa depois de a Fórmula MED consultar o pagamento na API com o seu Access Token.',
            exigido: false,
        },
        eventos: 'Pagamentos (tópico payment). A cobrança já informa a URL de notificação ao Mercado Pago.',
        documentacao: 'https://www.mercadopago.com.br/developers/pt/docs/checkout-pro/payment-notifications',
    },
];

export function descricaoDoGateway(provedor: ProvedorDeGateway): DescricaoDoGateway {
    return GATEWAYS_DE_PAGAMENTO.find((g) => g.id === provedor) ?? {
        id: provedor, nome: provedor, comoPaga: '', chave: { rotulo: 'Chave', dica: '' },
        segredo: { rotulo: 'Segredo do webhook', dica: '', exigido: true }, eventos: '', documentacao: '',
    };
}

/**
 * A URL que se cadastra no painel do gateway. É a da função de borda pública
 * `pagamentos-webhook`; sem servidor configurado não há URL a mostrar.
 */
export function urlDoWebhook(
    provedor: ProvedorDeGateway,
    base: string | undefined = import.meta.env.VITE_SUPABASE_URL as string | undefined,
): string | null {
    if (!base) return null;
    return `${base.replace(/\/+$/, '')}/functions/v1/pagamentos-webhook/${provedor}`;
}

/** As colunas da cobrança em `faturas` (0116). */
export const COLUNAS_DA_COBRANCA =
    'gateway, cobranca_link, cobranca_pix, cobranca_linha_digitavel, cobranca_expira_em, cobranca_erro, pago_por, forma_de_pagamento, recebido_a_menor_centavos';

type CamposDaCobranca = Pick<Fatura,
    'gateway' | 'cobrancaLink' | 'cobrancaPix' | 'cobrancaLinhaDigitavel' | 'cobrancaExpiraEm' | 'cobrancaErro' | 'pagoPor'
    | 'formaDePagamento' | 'recebidoAMenorCentavos'>;

export function camposDaCobranca(f: Record<string, unknown>): CamposDaCobranca {
    const campos: CamposDaCobranca = {};
    const texto = (v: unknown): string | undefined => (typeof v === 'string' && v !== '' ? v : undefined);
    if (f.gateway === 'asaas' || f.gateway === 'stripe' || f.gateway === 'mercadopago') campos.gateway = f.gateway;
    const link = texto(f.cobranca_link);
    if (link) campos.cobrancaLink = link;
    const pix = texto(f.cobranca_pix);
    if (pix) campos.cobrancaPix = pix;
    const linha = texto(f.cobranca_linha_digitavel);
    if (linha) campos.cobrancaLinhaDigitavel = linha;
    const expira = texto(f.cobranca_expira_em);
    if (expira) campos.cobrancaExpiraEm = expira;
    const erro = texto(f.cobranca_erro);
    if (erro) campos.cobrancaErro = erro;
    if (f.pago_por === 'manual' || f.pago_por === 'gateway') campos.pagoPor = f.pago_por;
    const forma = texto(f.forma_de_pagamento);
    if (forma) campos.formaDePagamento = forma;
    const aMenor = Number(f.recebido_a_menor_centavos ?? 0);
    if (Number.isFinite(aMenor) && aMenor > 0) campos.recebidoAMenorCentavos = aMenor;
    return campos;
}

/**
 * Pagamento menor que a fatura (0118): o que o gateway recebeu e o que falta. Não
 * quita — a fatura segue em aberto, e quem decide o resto é o operador.
 */
export function pagamentoMenor(f: Fatura): { recebidoCentavos: number; faltaCentavos: number } | null {
    if (f.pagoEm || f.estornadaEm || !f.recebidoAMenorCentavos) return null;
    return { recebidoCentavos: f.recebidoAMenorCentavos, faltaCentavos: Math.max(0, f.valorCentavos - f.recebidoAMenorCentavos) };
}

/** O jsonb de `minha_inadimplencia` (0117), ou `null` para a clínica em dia. */
export function paraInadimplencia(dado: unknown): InadimplenciaDaClinica | null {
    if (!dado || typeof dado !== 'object') return null;
    const d = dado as Record<string, unknown>;
    if (!d.inadimplenteDesde) return null;
    const f = d.fatura as Record<string, unknown> | null | undefined;
    return {
        inadimplenteDesde: String(d.inadimplenteDesde).slice(0, 10),
        bloqueioPrevistoEm: String(d.bloqueioPrevistoEm ?? '').slice(0, 10),
        ...(d.bloqueadaEm ? { bloqueadaEm: String(d.bloqueadaEm) } : {}),
        ...(f?.id ? {
            fatura: {
                id: String(f.id),
                competencia: String(f.competencia ?? ''),
                vencimento: String(f.vencimento ?? '').slice(0, 10),
                valorCentavos: Number(f.valorCentavos ?? 0),
            },
        } : {}),
    };
}

/** Uma linha sobre a cobrança da fatura, para a tabela do painel. */
export function rotuloDaCobranca(f: Fatura): string | null {
    const nome = f.gateway ? descricaoDoGateway(f.gateway).nome : null;
    if (f.pagoEm && f.pagoPor === 'gateway') {
        return `paga pelo ${nome ?? 'gateway'}${f.formaDePagamento ? ` · ${f.formaDePagamento}` : ''}`;
    }
    if (f.pagoEm) return 'baixa manual';
    if (f.estornadaEm) return null;
    if (nome && f.cobrancaLink) return `cobrança no ${nome}`;
    return null;
}
